import { useState, useCallback, useRef, useEffect } from "react";
import { Upload, X, Check, Loader2, Music, Sparkles, Play, Square, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface UploadItem {
  file: File;
  title: string;
  description: string;
  transcript: string;
  tags: string[];
  status: "pending" | "processing" | "uploading" | "done" | "error";
  progress: number;
  error?: string;
  duration_ms?: number;
  sample_rate?: number;
  r2_key?: string;
  waveform_key?: string;
  normalize: boolean;
  uploadMimeType?: string;
  transcribing?: boolean;
  publishing?: boolean;
}

export function AdminUpload() {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback((files: FileList) => {
    const newItems: UploadItem[] = Array.from(files)
      .filter((f) => f.type.startsWith("audio/"))
      .map((file) => ({
        file,
        title: file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "),
        description: "",
        transcript: "",
        tags: [],
        status: "pending" as const,
        progress: 0,
        normalize: true,
      }));

    setItems((prev) => [...prev, ...newItems]);

    for (const item of newItems) {
      processAudio(item);
    }
  }, []);

  const processAudio = async (item: UploadItem) => {
    updateItem(item.file.name, { status: "processing", progress: 10 });

    try {
      const arrayBuffer = await item.file.arrayBuffer();
      const audioCtx = new AudioContext();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

      const duration_ms = Math.round(audioBuffer.duration * 1000);
      const sample_rate = audioBuffer.sampleRate;

      const channelData = audioBuffer.getChannelData(0);
      const peaks = generatePeaks(channelData, 1024);

      let uploadData: ArrayBuffer = arrayBuffer;
      let uploadMimeType = item.file.type;
      if (item.normalize) {
        const result = await normalizeAudio(channelData, audioBuffer, audioCtx);
        uploadData = result.data;
        uploadMimeType = result.mimeType;
      }

      audioCtx.close();

      updateItem(item.file.name, {
        status: "uploading",
        progress: 40,
        duration_ms,
        sample_rate,
      });

      const urlRes = await api.admin.getUploadUrl();

      const audioBlob = new Blob([uploadData], { type: uploadMimeType });
      await fetch(`/api/admin/sounds/upload/${urlRes.audio_key}`, {
        method: "PUT",
        headers: { "Content-Type": uploadMimeType },
        body: audioBlob,
      });

      updateItem(item.file.name, { progress: 70 });

      await fetch(`/api/admin/sounds/upload/${urlRes.waveform_key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(peaks),
      });

      updateItem(item.file.name, {
        progress: 90,
        r2_key: urlRes.audio_key,
        waveform_key: urlRes.waveform_key,
        uploadMimeType,
      });

      updateItem(item.file.name, { status: "done", progress: 100 });
    } catch (err) {
      updateItem(item.file.name, {
        status: "error",
        error: err instanceof Error ? err.message : "Processing failed",
      });
    }
  };

  const publishSound = async (item: UploadItem) => {
    if (!item.r2_key || !item.duration_ms) return;
    updateItem(item.file.name, { publishing: true, error: undefined });

    try {
      await api.admin.createSound({
        title: item.title,
        description: item.description || undefined,
        transcript: item.transcript || undefined,
        tags: item.tags,
        r2_key: item.r2_key,
        waveform_key: item.waveform_key,
        duration_ms: item.duration_ms,
        file_size_bytes: item.file.size,
        mime_type: item.uploadMimeType || item.file.type,
        sample_rate: item.sample_rate,
        publish: true,
      });

      // Brief "Published!" flash before removing
      updateItem(item.file.name, { status: "done" as const, publishing: false });
      setTimeout(() => {
        setItems((prev) => prev.filter((i) => i.file.name !== item.file.name));
      }, 600);
    } catch (err) {
      updateItem(item.file.name, {
        publishing: false,
        error: err instanceof Error ? err.message : "Publish failed",
      });
    }
  };

  const updateItem = (filename: string, updates: Partial<UploadItem>) => {
    setItems((prev) =>
      prev.map((i) => (i.file.name === filename ? { ...i, ...updates } : i)),
    );
  };

  const removeItem = (filename: string) => {
    setItems((prev) => prev.filter((i) => i.file.name !== filename));
  };

  const handleTranscribe = async (item: UploadItem) => {
    updateItem(item.file.name, { transcribing: true });
    try {
      const res = await api.admin.transcribe(item.file);
      updateItem(item.file.name, { transcript: res.text, transcribing: false });
    } catch {
      updateItem(item.file.name, { transcribing: false });
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-2xl font-bold tracking-display">Upload Sounds</h1>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "mt-6 flex cursor-pointer flex-col items-center justify-center rounded-[18px] border-2 border-dashed p-12 transition-colors",
          dragOver
            ? "border-spark bg-spark/5"
            : "border-mist hover:border-spark/50",
        )}
      >
        <Upload className="h-8 w-8 text-fog" />
        <p className="mt-3 text-sm text-fog">
          Drop audio files here or click to browse
        </p>
        <p className="mt-1 font-mono text-[11px] text-fog/60">
          MP3, WAV, OGG, WebM, FLAC, AAC
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="audio/*"
          multiple
          onChange={(e) => {
            if (e.target.files?.length) addFiles(e.target.files);
            e.target.value = "";
          }}
          className="hidden"
        />
      </div>

      {items.length > 0 && (
        <div className="mt-6 space-y-4">
          {items.map((item) => (
            <div
              key={item.file.name}
              className="rounded-[18px] border border-mist bg-slate p-4"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-raised">
                  {item.status === "done" ? (
                    <Check className="h-5 w-5 text-moss" />
                  ) : item.status === "error" ? (
                    <X className="h-5 w-5 text-ember" />
                  ) : item.status === "pending" ? (
                    <Music className="h-5 w-5 text-fog" />
                  ) : (
                    <Loader2 className="h-5 w-5 animate-spin text-spark" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-parchment">{item.file.name}</span>
                    <button
                      onClick={() => removeItem(item.file.name)}
                      className="text-fog hover:text-parchment"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {item.status !== "pending" && item.status !== "done" && (
                    <div className="mt-2 h-1.5 rounded-full bg-mist">
                      <div
                        className="h-full rounded-full bg-spark transition-all"
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  )}

                  {item.error && (
                    <p className="mt-1 font-mono text-[11px] text-ember">{item.error}</p>
                  )}

                  {item.status === "done" && (
                    <UploadItemForm
                      item={item}
                      onUpdate={(updates) => updateItem(item.file.name, updates)}
                      onTranscribe={() => handleTranscribe(item)}
                      onPublish={() => publishSound(item)}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UploadItemForm({
  item,
  onUpdate,
  onTranscribe,
  onPublish,
}: {
  item: UploadItem;
  onUpdate: (updates: Partial<UploadItem>) => void;
  onTranscribe: () => void;
  onPublish: () => void;
}) {
  const [tagDraft, setTagDraft] = useState("");
  const tagInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const suggestingRef = useRef(false);

  const handleAiSuggest = async () => {
    if (!item.title && !item.transcript) return;
    if (suggestingRef.current) return;
    suggestingRef.current = true;
    setSuggesting(true);
    setSuggestError(null);
    try {
      const res = await api.admin.aiSuggest({
        title: item.title,
        transcript: item.transcript,
      });
      const updates: Partial<UploadItem> = {};
      if (res.description) updates.description = res.description;
      if (res.tags?.length) {
        const merged = [...new Set([...item.tags, ...res.tags])];
        updates.tags = merged;
      }
      onUpdate(updates);
    } catch (err) {
      setSuggestError(err instanceof Error ? err.message : "AI suggest failed — try again");
    } finally {
      setSuggesting(false);
      suggestingRef.current = false;
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const togglePreview = () => {
    if (!item.r2_key) return;

    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio(`/audio/${item.r2_key}`);
      audioRef.current.addEventListener("ended", () => setPlaying(false));
    }

    audioRef.current.play();
    setPlaying(true);
  };

  const addTag = (tag: string) => {
    const trimmed = tag.trim().toLowerCase();
    if (trimmed && !item.tags.includes(trimmed)) {
      onUpdate({ tags: [...item.tags, trimmed] });
    }
  };

  const removeTag = (tag: string) => {
    onUpdate({ tags: item.tags.filter((t) => t !== tag) });
  };

  return (
    <div className="mt-3 space-y-3">
      <div>
        <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
          Title
        </label>
        <input
          type="text"
          value={item.title}
          onChange={(e) => onUpdate({ title: e.target.value })}
          className="mt-1 w-full rounded-xl border border-mist bg-ink px-3 py-1.5 text-sm text-parchment outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
        />
        <div className="mt-1.5 flex items-center gap-2">
          <button
            onClick={handleAiSuggest}
            disabled={suggesting || (!item.title && !item.transcript)}
            className="flex items-center gap-1.5 rounded-xl bg-spark/10 px-3 py-1.5 text-sm font-medium text-spark transition-colors hover:bg-spark/20 disabled:opacity-50"
          >
            {suggesting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Wand2 className="h-3.5 w-3.5" />
            )}
            {suggesting ? "Generating…" : "AI suggest description & tags"}
          </button>
          {suggestError && (
            <span className="text-xs text-red-400">{suggestError}</span>
          )}
        </div>
      </div>

      <div>
        <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
          Description
        </label>
        <input
          type="text"
          value={item.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Optional short description"
          className="mt-1 w-full rounded-xl border border-mist bg-ink px-3 py-1.5 text-sm text-parchment placeholder:text-fog/40 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
        />
      </div>

      <div>
        <div className="flex items-center gap-2">
          <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
            Transcript
          </label>
          <button
            onClick={onTranscribe}
            disabled={item.transcribing}
            className="flex items-center gap-1 rounded-lg bg-spark/10 px-2 py-0.5 font-mono text-[10px] font-medium text-spark transition-colors hover:bg-spark/20 disabled:opacity-50"
          >
            {item.transcribing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            {item.transcribing ? "Transcribing…" : "Auto-transcribe"}
          </button>
        </div>
        <textarea
          value={item.transcript}
          onChange={(e) => onUpdate({ transcript: e.target.value })}
          rows={2}
          placeholder="What is said in this sound clip"
          className="mt-1 w-full rounded-xl border border-mist bg-ink px-3 py-1.5 text-sm text-parchment placeholder:text-fog/40 outline-none resize-none focus:border-spark focus:ring-2 focus:ring-spark/20"
        />
      </div>

      <div>
        <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
          Tags
        </label>
        <div
          onClick={() => tagInputRef.current?.focus()}
          className="mt-1 flex min-h-[38px] cursor-text flex-wrap items-center gap-1.5 rounded-xl border border-mist bg-ink px-2 py-1.5 focus-within:border-spark focus-within:ring-2 focus-within:ring-spark/20"
        >
          {item.tags.map((tag) => (
            <span
              key={tag}
              className="flex items-center gap-1 rounded-lg bg-spark/15 px-2 py-0.5 text-xs text-spark"
            >
              {tag}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                className="text-spark/60 hover:text-spark"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            ref={tagInputRef}
            type="text"
            value={tagDraft}
            onChange={(e) => {
              const val = e.target.value;
              if (val.includes(",")) {
                const parts = val.split(",");
                for (const part of parts) {
                  addTag(part);
                }
                setTagDraft("");
                return;
              }
              setTagDraft(val);
            }}
            onKeyDown={(e) => {
              if (e.key === "Tab" || e.key === "Enter") {
                const trimmed = tagDraft.trim().toLowerCase();
                if (trimmed && !item.tags.includes(trimmed)) {
                  e.preventDefault();
                  addTag(tagDraft);
                  setTagDraft("");
                } else if (trimmed) {
                  e.preventDefault();
                  setTagDraft("");
                }
              } else if (
                e.key === "Backspace" &&
                !tagDraft &&
                item.tags.length > 0
              ) {
                removeTag(item.tags[item.tags.length - 1]);
              }
            }}
            placeholder={item.tags.length === 0 ? "Add tags…" : ""}
            className="min-w-[60px] flex-1 bg-transparent text-sm text-parchment placeholder:text-fog/40 outline-none"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={onPublish}
          disabled={item.publishing}
          className={cn(
            "flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors disabled:opacity-70",
            item.publishing
              ? "bg-moss text-ink"
              : "bg-spark text-ink hover:bg-spark-soft",
          )}
        >
          {item.publishing ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Publishing...
            </>
          ) : (
            "Publish sound"
          )}
        </button>
        <button
          onClick={togglePreview}
          className="flex items-center gap-1.5 rounded-xl border border-mist px-3 py-2 text-sm font-medium text-parchment transition-colors hover:border-spark/50 hover:bg-spark/5"
        >
          {playing ? (
            <Square className="h-3.5 w-3.5 fill-current" />
          ) : (
            <Play className="h-3.5 w-3.5 fill-current" />
          )}
          {playing ? "Stop" : "Preview"}
        </button>
      </div>
    </div>
  );
}

function generatePeaks(channelData: Float32Array, numPeaks: number): number[] {
  const peaks: number[] = [];
  const samplesPerPeak = Math.floor(channelData.length / numPeaks);

  for (let i = 0; i < numPeaks; i++) {
    let max = 0;
    const start = i * samplesPerPeak;
    const end = Math.min(start + samplesPerPeak, channelData.length);

    for (let j = start; j < end; j++) {
      const abs = Math.abs(channelData[j]);
      if (abs > max) max = abs;
    }

    peaks.push(Math.round(max * 1000) / 1000);
  }

  return peaks;
}

function encodeWav(audioBuffer: AudioBuffer): ArrayBuffer {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const numSamples = audioBuffer.length;
  const bytesPerSample = 2;
  const dataSize = numSamples * numChannels * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = audioBuffer.getChannelData(ch)[i];
      const clamped = Math.max(-1, Math.min(1, sample));
      view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
      offset += 2;
    }
  }

  return buffer;
}

async function normalizeAudio(
  channelData: Float32Array,
  buffer: AudioBuffer,
  _ctx: AudioContext,
): Promise<{ data: ArrayBuffer; mimeType: string }> {
  let peak = 0;
  for (let i = 0; i < channelData.length; i++) {
    const abs = Math.abs(channelData[i]);
    if (abs > peak) peak = abs;
  }

  const targetPeak = Math.pow(10, -1 / 20);
  const gain = peak > 0 ? targetPeak / peak : 1;

  if (Math.abs(gain - 1) < 0.01) {
    return { data: encodeWav(buffer), mimeType: "audio/wav" };
  }

  const offlineCtx = new OfflineAudioContext(
    buffer.numberOfChannels,
    buffer.length,
    buffer.sampleRate,
  );

  const source = offlineCtx.createBufferSource();
  source.buffer = buffer;

  const gainNode = offlineCtx.createGain();
  gainNode.gain.value = gain;

  source.connect(gainNode);
  gainNode.connect(offlineCtx.destination);
  source.start();

  const rendered = await offlineCtx.startRendering();
  return { data: encodeWav(rendered), mimeType: "audio/wav" };
}
